/**
 * Filter Drawer for Mobile & iPad
 * Handles drawer open/close, filter selections, and price range sliders
 */

class FilterDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-filter-drawer]');
    if (!this.drawer) return;
    
    // Elements
    this.trigger = document.querySelector('[data-filter-drawer-trigger]');
    this.overlay = document.querySelector('[data-filter-drawer-overlay]');
    this.closeBtn = document.querySelector('[data-filter-drawer-close]');
    this.applyBtn = document.querySelector('[data-filter-drawer-apply]');
    this.clearBtn = document.querySelector('[data-filter-clear-all]');
    this.badge = document.querySelector('[data-filter-badge]');
    this.applyCount = document.querySelector('[data-apply-count]');
    this.container = this.drawer.querySelector('.filter-drawer__container');
    
    // State
    this.isOpen = false;
    this.activeFilters = 0;
    this.initialState = null;
    
    this.init();
  }
  
  init() {
    // Save initial state from URL parameters
    this.saveInitialState();
    
    // Sync drawer with URL parameters on load
    this.syncWithURL();
    
    // Event Listeners
    this.trigger?.addEventListener('click', () => this.open());
    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.applyBtn?.addEventListener('click', () => this.apply());
    this.clearBtn?.addEventListener('click', () => this.clearAll());
    
    // Prevent drawer close when clicking inside
    this.container?.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    // Track filter changes
    this.drawer.querySelectorAll('[data-filter-checkbox]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateBadge();
        this.preventAccordionClose(checkbox);
      });
    });
    
    // Track price input changes
    this.drawer.querySelectorAll('[data-price-min], [data-price-max]').forEach(input => {
      input.addEventListener('input', () => this.updateBadge());
      input.addEventListener('change', () => this.updateBadge());
    });
    
    // Initialize features
    this.initPriceSliders();
    this.initAccordionBehavior();
    this.initKeyboardNav();
    
    // Update badge AFTER everything is initialized
    setTimeout(() => {
      this.updateBadge();
    }, 100);
    
    console.log('✅ Filter Drawer initialized');
  }
  
  /**
   * Save initial state from URL parameters
   */
  saveInitialState() {
    const url = new URL(window.location.href);
    this.initialState = {
      params: url.searchParams
    };
  }
  
  /**
   * Sync drawer filters with current URL parameters
   */
  syncWithURL() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    
    // First, uncheck ALL checkboxes
    this.drawer.querySelectorAll('[data-filter-checkbox]').forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Then check only the ones in URL
    this.drawer.querySelectorAll('[data-filter-checkbox]').forEach(checkbox => {
      const paramName = checkbox.name;
      const paramValue = checkbox.value;
      
      // Check if this filter is in URL
      const urlValues = params.getAll(paramName);
      if (urlValues.includes(paramValue)) {
        checkbox.checked = true;
      }
    });
    
    // Sync price range from URL
    const priceMinInput = this.drawer.querySelector('[data-price-min]');
    const priceMaxInput = this.drawer.querySelector('[data-price-max]');
    
    if (priceMinInput) {
      const urlMinValue = params.get(priceMinInput.name);
      if (urlMinValue) {
        priceMinInput.value = urlMinValue;
      } else {
        priceMinInput.value = '';
      }
    }
    
    if (priceMaxInput) {
      const urlMaxValue = params.get(priceMaxInput.name);
      if (urlMaxValue) {
        priceMaxInput.value = urlMaxValue;
      } else {
        priceMaxInput.value = '';
      }
    }
    
    console.log('🔄 Synced filters with URL');
  }
  
  /**
   * Open the drawer
   */
  open() {
    // Re-sync with URL before opening to ensure correct state
    this.syncWithURL();
    
    this.drawer.classList.add('is-open');
    document.body.classList.add('filter-drawer-open');
    this.isOpen = true;
    
    // Update badge count
    this.updateBadge();
    
    // Trap focus inside drawer
    this.trapFocus();
    
    console.log('🔓 Filter drawer opened');
  }
  
  /**
   * Close the drawer
   */
  close() {
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('filter-drawer-open');
    this.isOpen = false;
    
    console.log('🔒 Filter drawer closed');
  }
  
  /**
   * Apply filters and close drawer
   */
  apply() {
    console.log('✅ Applying filters...');
    
    // Get the collection filter form
    const form = document.querySelector('form[data-collection-filters-form]') || 
                 document.querySelector('.collection-filters-form') ||
                 document.querySelector('form.filters-form') ||
                 document.querySelector('[data-collection-form]');
    
    if (form) {
      // Copy drawer selections to desktop form before submitting
      this.syncDesktopForm(form);
      
      // Trigger form submission
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(submitEvent);
      console.log('📤 Form submitted');
    } else {
      // Alternative: trigger URL update with current filters
      this.applyFiltersViaURL();
    }
    
    this.close();
  }
  
  /**
   * Sync desktop form with drawer selections
   */
  syncDesktopForm(form) {
    // First, uncheck all checkboxes in desktop form
    form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Then check the ones selected in drawer
    this.drawer.querySelectorAll('[data-filter-checkbox]:checked').forEach(drawerCheckbox => {
      const desktopCheckbox = form.querySelector(`input[name="${drawerCheckbox.name}"][value="${drawerCheckbox.value}"]`);
      if (desktopCheckbox) {
        desktopCheckbox.checked = true;
      }
    });
    
    // Sync price inputs
    const drawerPriceMin = this.drawer.querySelector('[data-price-min]');
    const drawerPriceMax = this.drawer.querySelector('[data-price-max]');
    
    if (drawerPriceMin) {
      const desktopPriceMin = form.querySelector(`input[name="${drawerPriceMin.name}"]`);
      if (desktopPriceMin) {
        desktopPriceMin.value = drawerPriceMin.value;
      }
    }
    
    if (drawerPriceMax) {
      const desktopPriceMax = form.querySelector(`input[name="${drawerPriceMax.name}"]`);
      if (desktopPriceMax) {
        desktopPriceMax.value = drawerPriceMax.value;
      }
    }
  }
  
  /**
   * Apply filters by updating URL (fallback method)
   */
  applyFiltersViaURL() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();
    
    // Keep sorting parameter if it exists
    const currentSort = url.searchParams.get('sort_by');
    if (currentSort) {
      params.append('sort_by', currentSort);
    }
    
    // Collect checked filters from drawer
    this.drawer.querySelectorAll('[data-filter-checkbox]:checked').forEach(checkbox => {
      params.append(checkbox.name, checkbox.value);
    });
    
    // Collect price range from drawer
    const priceMin = this.drawer.querySelector('[data-price-min]');
    const priceMax = this.drawer.querySelector('[data-price-max]');
    
    if (priceMin && priceMin.value && priceMin.value !== '0') {
      params.append(priceMin.name, priceMin.value);
    }
    
    if (priceMax && priceMax.value) {
      params.append(priceMax.name, priceMax.value);
    }
    
    // Update URL and reload
    url.search = params.toString();
    window.location.href = url.toString();
  }
  
  /**
   * Clear all filters
   */
  clearAll() {
    console.log('🧹 Clearing all filters...');
    
    // Uncheck all checkboxes in drawer
    this.drawer.querySelectorAll('[data-filter-checkbox]:checked').forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Clear price inputs in drawer
    this.drawer.querySelectorAll('[data-price-min], [data-price-max]').forEach(input => {
      input.value = '';
    });
    
    // Reset price sliders
    this.drawer.querySelectorAll('[data-range-slider]').forEach(slider => {
      const min = parseFloat(slider.dataset.min);
      const max = parseFloat(slider.dataset.max);
      
      slider.dataset.minValue = min;
      slider.dataset.maxValue = max;
      
      this.updateSliderUI(slider, min, max, min, max);
    });
    
    this.updateBadge();
    
    // Apply to clear filters on page
    this.apply();
  }
  
  /**
   * Update badge with active filter count
   */
  updateBadge() {
    // Count checked checkboxes in drawer
    const checkedCount = this.drawer.querySelectorAll('[data-filter-checkbox]:checked').length;
    
    // Count filled price inputs (only if they have meaningful values)
    const priceMin = this.drawer.querySelector('[data-price-min]');
    const priceMax = this.drawer.querySelector('[data-price-max]');
    let priceFilterActive = false;
    
    // Check if price filter has actual values (not empty, not 0, not placeholder)
    if (priceMin && priceMin.value && priceMin.value !== '' && parseFloat(priceMin.value) > 0) {
      priceFilterActive = true;
    }
    
    if (priceMax && priceMax.value && priceMax.value !== '') {
      const maxVal = parseFloat(priceMax.value);
      const rangeMax = parseFloat(priceMax.max);
      // Only count if it's not the maximum value
      if (maxVal < rangeMax) {
        priceFilterActive = true;
      }
    }
    
    this.activeFilters = checkedCount + (priceFilterActive ? 1 : 0);
    
    console.log(`🔢 Calculating badge: checkboxes=${checkedCount}, priceActive=${priceFilterActive}, total=${this.activeFilters}`);
    
    // Update badge on trigger button
    if (this.badge) {
      if (this.activeFilters > 0) {
        this.badge.textContent = this.activeFilters;
        this.badge.style.display = 'block';
      } else {
        this.badge.style.display = 'none';
      }
    }
    
    // Update apply button count
    if (this.applyCount) {
      if (this.activeFilters > 0) {
        this.applyCount.textContent = `(${this.activeFilters})`;
        this.applyCount.style.display = 'inline-block';
      } else {
        this.applyCount.style.display = 'none';
      }
    }
    
    // Update individual filter item counts
    this.drawer.querySelectorAll('[data-filter-item]').forEach(filterItem => {
      const countEl = filterItem.querySelector('.filter-drawer__item-count');
      const checkedInGroup = filterItem.querySelectorAll('[data-filter-checkbox]:checked').length;
      
      if (countEl) {
        if (checkedInGroup > 0) {
          countEl.textContent = checkedInGroup;
          countEl.style.display = 'inline-block';
        } else {
          countEl.style.display = 'none';
        }
      }
    });
  }
  
  /**
   * Prevent accordion from closing when checkbox is clicked
   */
  preventAccordionClose(checkbox) {
    const details = checkbox.closest('details');
    if (details) {
      // Keep it open
      setTimeout(() => {
        details.setAttribute('open', '');
      }, 10);
    }
  }
  
  /**
   * Initialize accordion behavior
   */
  initAccordionBehavior() {
    this.drawer.querySelectorAll('.filter-drawer__item').forEach(item => {
      const summary = item.querySelector('.filter-drawer__item-title');
      
      summary?.addEventListener('click', (e) => {
        // Allow normal details behavior
        // Just stop propagation to prevent closing drawer
        e.stopPropagation();
      });
    });
  }
  
  /**
   * Initialize price range sliders
   */
  initPriceSliders() {
    const sliders = this.drawer.querySelectorAll('[data-range-slider]');
    
    sliders.forEach(slider => {
      const min = parseFloat(slider.dataset.min);
      const max = parseFloat(slider.dataset.max);
      let minValue = parseFloat(slider.dataset.minValue) || min;
      let maxValue = parseFloat(slider.dataset.maxValue) || max;
      
      const thumbMin = slider.querySelector('[data-range-thumb-min]');
      const thumbMax = slider.querySelector('[data-range-thumb-max]');
      const fill = slider.querySelector('[data-range-fill]');
      
      const filterContent = slider.closest('.filter-drawer__item-content');
      const inputMin = filterContent?.querySelector('[data-price-min]');
      const inputMax = filterContent?.querySelector('[data-price-max]');
      
      // Update slider UI
      const updateSlider = () => {
        this.updateSliderUI(slider, min, max, minValue, maxValue);
        
        // Only set values if they're different from defaults
        if (inputMin) {
          if (minValue > min) {
            inputMin.value = Math.round(minValue);
          } else {
            inputMin.value = '';
          }
        }
        
        if (inputMax) {
          if (maxValue < max) {
            inputMax.value = Math.round(maxValue);
          } else {
            inputMax.value = '';
          }
        }
        
        this.updateBadge();
      };
      
      // Handle thumb dragging
      this.initThumbDrag(thumbMin, slider, min, max, (value) => {
        minValue = Math.min(value, maxValue - (max - min) * 0.01);
        updateSlider();
      });
      
      this.initThumbDrag(thumbMax, slider, min, max, (value) => {
        maxValue = Math.max(value, minValue + (max - min) * 0.01);
        updateSlider();
      });
      
      // Sync inputs with slider
      inputMin?.addEventListener('input', (e) => {
        const newValue = parseFloat(e.target.value);
        if (!isNaN(newValue) && newValue > 0) {
          minValue = Math.max(min, Math.min(newValue, maxValue));
        } else {
          minValue = min;
          e.target.value = '';
        }
        this.updateSliderUI(slider, min, max, minValue, maxValue);
        this.updateBadge();
      });
      
      inputMax?.addEventListener('input', (e) => {
        const newValue = parseFloat(e.target.value);
        if (!isNaN(newValue)) {
          maxValue = Math.min(max, Math.max(newValue, minValue));
        } else {
          maxValue = max;
          e.target.value = '';
        }
        this.updateSliderUI(slider, min, max, minValue, maxValue);
        this.updateBadge();
      });
      
      // Initial render - sync with current input values
      if (inputMin && inputMin.value && parseFloat(inputMin.value) > 0) {
        minValue = parseFloat(inputMin.value);
      } else {
        minValue = min;
      }
      
      if (inputMax && inputMax.value) {
        maxValue = parseFloat(inputMax.value);
      } else {
        maxValue = max;
      }
      
      updateSlider();
    });
    
    console.log('📊 Price sliders initialized');
  }
  
  /**
   * Update slider visual UI
   */
  updateSliderUI(slider, min, max, minValue, maxValue) {
    const thumbMin = slider.querySelector('[data-range-thumb-min]');
    const thumbMax = slider.querySelector('[data-range-thumb-max]');
    const fill = slider.querySelector('[data-range-fill]');
    
    const percentMin = ((minValue - min) / (max - min)) * 100;
    const percentMax = ((maxValue - min) / (max - min)) * 100;
    
    if (thumbMin) thumbMin.style.left = `${percentMin}%`;
    if (thumbMax) thumbMax.style.left = `${percentMax}%`;
    if (fill) {
      fill.style.left = `${percentMin}%`;
      fill.style.right = `${100 - percentMax}%`;
    }
  }
  
  /**
   * Initialize thumb dragging behavior
   */
  initThumbDrag(thumb, slider, min, max, onUpdate) {
    if (!thumb) return;
    
    let isDragging = false;
    
    const startDrag = (e) => {
      isDragging = true;
      e.preventDefault();
      document.body.style.userSelect = 'none';
    };
    
    const onMove = (e) => {
      if (!isDragging) return;
      
      const rect = slider.getBoundingClientRect();
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      const x = clientX - rect.left;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
      const value = min + (percent / 100) * (max - min);
      
      onUpdate(value);
    };
    
    const endDrag = () => {
      isDragging = false;
      document.body.style.userSelect = '';
    };
    
    // Mouse events
    thumb.addEventListener('mousedown', startDrag);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', endDrag);
    
    // Touch events
    thumb.addEventListener('touchstart', startDrag, { passive: false });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', endDrag);
  }
  
  /**
   * Trap focus inside drawer when open
   */
  trapFocus() {
    const focusableElements = this.container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    
    const handleTabKey = (e) => {
      if (!this.isOpen || e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          lastFocusable?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          firstFocusable?.focus();
          e.preventDefault();
        }
      }
    };
    
    document.addEventListener('keydown', handleTabKey);
  }
  
  /**
   * Keyboard navigation
   */
  initKeyboardNav() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.filterDrawer = new FilterDrawer();
  });
} else {
  window.filterDrawer = new FilterDrawer();
}